import { useQuery } from "@apollo/client";
import { shuffle } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import {
  ArtifactType,
  GetAskAiPromptSuggestionsQueryQuery,
  GetAskAiPromptSuggestionsQueryQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

import getAskAiPromptSuggestionsQuery from "../graphql/get-ask-ai-prompt-suggestions-query";
import {
  SuggestionMeeting,
  buildPromptSuggestions,
  buildSkillSuggestions,
} from "./ask-ai-prompt-suggestions-helpers";

// How far ahead we look for a meeting to offer prep for, and how many meetings
// we fetch to find the next 1-on-1 and the next group meeting.
const suggestionDaysAhead = 7;
const suggestionMeetingMax = 10;

/** Starter prompts under the homepage Ask AI input (TF-1590), so a new user has
 * something concrete to click instead of an empty box. Prep for the user's own
 * next meetings comes first; when there is no meeting to prep, we offer the
 * skill prompts (daily brief, recognition/feedback opportunities) instead. */
const AskAIPromptSuggestions = ({
  onClickSuggestion,
}: {
  onClickSuggestion: (prompt: string) => void;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const hasMeetings = !!currentOrganization.featureFlags.meetings;

  // Rounded to the hour so the variables (and the cache key) stay stable while
  // the homepage is open, and so a meeting already in progress is included.
  const windowStart = moment().startOf("hour");
  const skipMeetings = !hasMeetings || !currentUser.id;

  const { data, loading } = useQuery<
    GetAskAiPromptSuggestionsQueryQuery,
    GetAskAiPromptSuggestionsQueryQueryVariables
  >(getAskAiPromptSuggestionsQuery, {
    variables: {
      participants: [currentUser.id],
      startDatetimeGte: windowStart.format(),
      startDatetimeLte: moment(windowStart)
        .add(suggestionDaysAhead, "days")
        .endOf("day")
        .format(),
      meetingMax: suggestionMeetingMax,
    },
    skip: skipMeetings,
    fetchPolicy: "cache-and-network",
  });

  const meetings: SuggestionMeeting[] = useMemo(
    () =>
      (data?.meetings?.edges || []).flatMap((edge) => {
        const meeting = edge?.node;
        if (!meeting) return [];
        const otherParticipants = (meeting.participants?.edges || []).flatMap(
          (participantEdge) => {
            const user = participantEdge?.node?.user;
            if (!user || user.id === currentUser.id) return [];
            return [
              { id: user.id, name: user.name, firstName: user.firstName },
            ];
          },
        );
        return [
          {
            id: meeting.id,
            title: meeting.title,
            startDatetime: meeting.startDatetime,
            endDatetime: meeting.endDatetime,
            isFormalOneonone: !!meeting.meetingGroup?.isFormalOneonone,
            otherParticipants,
          },
        ];
      }),
    [data, currentUser.id],
  );

  // Shuffled once per mount so the prompts vary between visits without the
  // chips reordering themselves as the page re-renders.
  const recognitionLabel = label(ArtifactType.Recognition);
  const skillSuggestions = useMemo(
    () => shuffle(buildSkillSuggestions({ recognitionLabel })),
    [recognitionLabel],
  );

  // Wait for the meetings to land before rendering, otherwise the skill prompts
  // show first and are replaced a moment later.
  if (!skipMeetings && loading && !data) return null;

  const suggestions = buildPromptSuggestions({
    meetings,
    skillSuggestions,
    oneononeLabel: label("oneonone"),
    now: moment(),
  });

  if (suggestions.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="Topicflow AI suggestions"
      className="mt-3 flex flex-wrap justify-center gap-1.5"
    >
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.key}
          type="button"
          onClick={() => onClickSuggestion(suggestion.prompt)}
          className="flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 hover:border-gray-300"
        >
          <TopicflowIcon
            type={suggestion.icon}
            className="h-3.5 w-3.5 shrink-0 text-violet-500"
          />
          {suggestion.label}
        </button>
      ))}
    </div>
  );
};

export default AskAIPromptSuggestions;
